import React from 'react'
import TextareaAutosize from 'react-textarea-autosize'

import useKeyDown from '../hooks/use-key-down'
import {makeRequest} from '../http'
import genId from '../utils/gen_id'
import AddIcon from './add-icon'
import Button from './button'
import CloseIcon from './close-icon'
import DeleteIcon from './trashcan2-icon'
import EyeIcon from './eye-icon'
import EyeOffIcon from './eye-off-icon'
import Modal from './modal'
import {ObjectContext} from './object-store'
import {useDelete} from './object-store'
import {useFocusBlurSaveField} from './object-store'
import {useList} from './object-store'
import {useObjectStore} from './object-store'
import {useSave} from './object-store'
import {useSaveField} from './object-store'
import {useSaves} from './object-store'
import Portal from './portal'
import './modal.scss'
import './templates-modal.scss'

const tempaltesRequest = {sort: [{field: 'template.rank', dir: 'ASC'}]}


export default function TemplatesModal({onClose, onSchedule}) {
    const save = useSave()
    const saves = useSaves()
    const {state} = useObjectStore()
    const [showHidden, setShowHidden] = React.useState(false)
    const {ids, refresh} = useList('template', tempaltesRequest)
    const [focusId, setFocusId] = React.useState(null)

    function handleAdd() {
        const id = genId()
        save('template', {id, text: ''})
            .then(() => {
                setFocusId(id)
                refresh()
            })
    }

    function handleMoveUp(id) {
        const index = ids.indexOf(id)
        if (index <= 0) return
        const prevId = ids[index - 1]
        saves('template', [
            {id, rank: state[prevId]?.rank},
            {id: prevId, rank: state[id]?.rank},
        ]).then(refresh)
    }

    function handleMoveDown(id) {
        const index = ids.indexOf(id)
        if (index >= ids.length - 1) return
        const nextId = ids[index + 1]
        saves('template', [
            {id, rank: state[nextId]?.rank},
            {id: nextId, rank: state[id]?.rank},
        ]).then(refresh)
    }

    const visibleIds = showHidden
        ? ids.filter(id => state[id]?.hidden)
        : ids.filter(id => !state[id]?.hidden)

    return <Modal stretch onClose={onClose} nopad>
        <div className="templates-modal">
            <div className="templates-modal__toolbar">
                <Button onClick={handleAdd}><AddIcon color="white" /></Button>
                <Button onClick={() => setShowHidden(prev => !prev)} active={showHidden}><EyeOffIcon /></Button>
                <Button onClick={onClose} background={null}><CloseIcon /></Button>
            </div>
            <div className="templates-modal__templates">
                {visibleIds.map(id => {
                    const index = ids.indexOf(id)
                    return <TemplateEditor
                        id={id}
                        key={id}
                        autoFocus={id === focusId}
                        onRemove={refresh}
                        onSchedule={onSchedule}
                        canMoveUp={index > 0}
                        canMoveDown={index < ids.length - 1}
                        onMoveUp={() => handleMoveUp(id)}
                        onMoveDown={() => handleMoveDown(id)}
                    />
                })}
            </div>
        </div>
    </Modal>
}

function TemplateEditor({id, onRemove, onSchedule, autoFocus, canMoveUp, canMoveDown, onMoveUp, onMoveDown}) {
    return <ObjectContext model="template" id={id}>
        <TemplateEditor_ onRemove={onRemove} onSchedule={onSchedule} id={id} autoFocus={autoFocus} canMoveUp={canMoveUp} canMoveDown={canMoveDown} onMoveUp={onMoveUp} onMoveDown={onMoveDown} />
    </ObjectContext>
}

function TemplateEditor_({id, onRemove, onSchedule, autoFocus, canMoveUp, canMoveDown, onMoveUp, onMoveDown}) {
    const textProps = useFocusBlurSaveField('text')
    const [hidden, saveHidden] = useSaveField('hidden')
    const remove = useDelete()
    const textareaRef = React.useRef()

    React.useEffect(() => {
        if (autoFocus && textareaRef.current) {
            textareaRef.current.scrollIntoView({behavior: 'smooth', block: 'nearest'})
            textareaRef.current.focus()
        }
    }, [])
    const [schedState, setSchedState] = React.useState()
    const [showSchedulePicker, setShowSchedulePicker] = React.useState(false)

    function getSelectedText() {
        const el = textareaRef.current
        if (el && el.selectionStart !== el.selectionEnd) {
            return el.value.slice(el.selectionStart, el.selectionEnd)
        }
        return textProps.value
    }

    function scheduleText(text, day) {
        setSchedState('doing')
        makeRequest('schedule_template', {
            body: JSON.stringify({text, day})
        })
            .then(() => {
                onSchedule()
                setSchedState('done')
                setTimeout(() => setSchedState(null), 1000)
            })
    }

    function handleToday() {
        scheduleText(getSelectedText(), new Date().toISOString().split('T')[0])
    }

    return <div className="templates-modal__template">
        <TextareaAutosize
            {...textProps}
            ref={textareaRef}
            placeholder={'# Section title\nExercise name'}
        />
        <div className="spacer spacer--space-between">
            <div className="spacer spacer--gap2">
                <Button
                    dangerHover
                    onClick={() => remove('template', id).then(onRemove)}
                >
                    <DeleteIcon color="white" />
                </Button>
                <Button onClick={onMoveUp} disabled={!canMoveUp}>↑</Button>
                <Button onClick={onMoveDown} disabled={!canMoveDown}>↓</Button>
                <Button onClick={() => saveHidden(hidden ? 0 : 1)} active={!!hidden}>
                    {hidden ? <EyeOffIcon /> : <EyeIcon />}
                </Button>
            </div>
            <div className="spacer spacer--gap2">
                <Button onClick={() => setShowSchedulePicker(true)}>Schedule</Button>
                <Button
                    onClick={handleToday}
                    disabled={schedState === 'doing'}
                >
                    {schedState === 'done' ? '✓ T' : 'T'}
                </Button>
            </div>
        </div>
        {showSchedulePicker &&
            <SchedulePicker
                onConfirm={day => {
                    setShowSchedulePicker(false)
                    scheduleText(getSelectedText(), day)
                }}
                onClose={() => setShowSchedulePicker(false)}
            />
        }
    </div>
}

function SchedulePicker({onConfirm, onClose}) {
    const [day, setDay] = React.useState(new Date().toISOString().split('T')[0])
    const ref = React.useRef()
    const mouseDownRef = React.useRef(false)
    const dateInputRef = React.useRef()

    React.useEffect(() => {
        if (dateInputRef.current) dateInputRef.current.focus()
    }, [])

    useKeyDown(event => {
        if (event.key === 'Enter') onConfirm(day)
        if (event.key === 'Escape') onClose()
    })

    function handleMouseDown(event) {
        if (event.target === ref.current) mouseDownRef.current = true
    }

    function handleClick(event) {
        event.stopPropagation()
        if (event.target === ref.current && mouseDownRef.current) onClose()
        mouseDownRef.current = false
    }

    return <Portal>
        <div
            className="executor"
            ref={ref}
            onMouseDown={handleMouseDown}
            onClick={handleClick}
        >
            <div className="executor__inner">
                <input
                    type="date"
                    value={day}
                    onChange={e => setDay(e.target.value)}
                    ref={dateInputRef}
                />
                <Button onClick={() => onConfirm(day)}>Schedule</Button>
            </div>
        </div>
    </Portal>
}
